/** Card surface. Every white panel in the designs is one of these. */

import React, { memo } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { colors, radius, shadow, space } from '../../theme';
import { Text } from './Text';

export interface CardProps {
  children: React.ReactNode;
  /** Optional heading rendered inside the card. */
  title?: string;
  /** Right-aligned action beside the title, e.g. the "Edit" links. */
  action?: React.ReactNode;
  /** Removes internal padding when the content manages its own, e.g. images. */
  flush?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const Card = memo(function Card({
  children,
  title,
  action,
  flush = false,
  style,
  testID,
}: CardProps) {
  return (
    <View testID={testID} style={[styles.card, flush && styles.flush, style]}>
      {title || action ? (
        <View style={[styles.header, flush && styles.headerFlush]}>
          {title ? (
            <Text variant="sectionTitle" style={styles.title}>
              {title}
            </Text>
          ) : (
            <View style={styles.title} />
          )}
          {action}
        </View>
      ) : null}
      {children}
    </View>
  );
});

/** Label/value row used by receipts, summaries and detail panels. */
export const CardRow = memo(function CardRow({
  label,
  value,
  emphasis = false,
  testID,
}: {
  label: string;
  value: React.ReactNode;
  /** Totals are emphasised: bold label, brand-coloured value. */
  emphasis?: boolean;
  testID?: string;
}) {
  return (
    <View style={styles.rowContainer} testID={testID}>
      <Text variant={emphasis ? 'bodyStrong' : 'body'} tone={emphasis ? 'primary' : 'secondary'}>
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Text
          variant={emphasis ? 'amountSmall' : 'bodyStrong'}
          tone={emphasis ? 'brand' : 'primary'}
        >
          {value}
        </Text>
      ) : (
        value
      )}
    </View>
  );
});

export const CardDivider = memo(function CardDivider() {
  return <View style={styles.divider} />;
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    ...shadow('card'),
  },
  flush: {
    padding: 0,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: space.md,
  },
  headerFlush: {
    paddingHorizontal: space.lg,
    paddingTop: space.lg,
  },
  title: {
    flexShrink: 1,
  },
  rowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: space.md,
    gap: space.md,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border.subtle,
  },
});
